import React, { FC, useEffect, useRef, useState } from "react";
import './Clauses.scss';
import Clause from "./Clause";
import { IClause } from "../../Interfaces";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ReactSVG } from "react-svg";
import DragIcon from "../../../../Assets/icons/paragraph-spacing.svg";
import DeleteIcon from "../../../../Assets/icons/delete.svg";
import AddIcon from "../../../../Assets/icons/plus.svg";
import TextEdit from "../../../../Components/TextEdit/TextEdit";
import { useSelector } from "react-redux";
import { selectRedraftingClause } from "../../../../Store/draftingSlice";
import { useTranslation } from "react-i18next";
import NoDataScreen, { ENDSType } from "../NoDataScreen/NoDataScreen";

interface IClausesProps {
    isDrafting?: boolean;
    clauses?: IClause[];
    documentId: number;
    setClauses?: (clauses: IClause[]) => void;
    updateSequence?: (result: any) => void;
    addNewClauseAtSequence?: (sequence: number) => void;
    updateClauseBySequence?: (sequence: number, clause: IClause) => void;
    deleteClauseById?: (id: number) => void;
}

const Clauses: FC<IClausesProps> = ({
    isDrafting,
    clauses,
    updateSequence,
    updateClauseBySequence,
    addNewClauseAtSequence,
    deleteClauseById,
}) => {
    const clauseListRef = useRef<HTMLDivElement>(null);
    
    const onDragEndClause = (result: any) => {
        updateSequence(result)
    }

    const [ instructionsRequired, setInstructionsRequired ] = useState<boolean>(false);

    const redraftingClause = useSelector(selectRedraftingClause);

    useEffect(() => {
        if(isDrafting) {
            const required = clauses.find((element) => (element?.instructions?.length > 0 && element.instructions_complete === false));
            setInstructionsRequired(!!required);
        }
    }, [clauses, isDrafting, redraftingClause]);

    const { t } = useTranslation();

    function replaceDynamicTagContent(originalString, newValue) {
        const tagMatch = originalString.match(/<([a-zA-Z0-9]+)>/);
        if (!tagMatch) {
            return newValue;
        }

        const tagName = tagMatch[1];

        return `<${tagName}>${newValue}</${tagName}>`;
    }

    if(clauses.length === 0) {
        return (
            <div className="clauses-nds">
                <NoDataScreen type={ENDSType.NO_CLAUSE} onClick={() => addNewClauseAtSequence(0)} />
            </div>
        )
    }

    return (
        <div className="clauses-container">
            <DragDropContext 
                onDragEnd={onDragEndClause}
            >
                <Droppable droppableId="clauses">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={(node) => {
                            provided.innerRef(node);
                            clauseListRef.current = node;
                        }}>
                            {clauses.map((clause, index) => {
                                const onTitleChange = (value: string) =>{
                                    const newTitle = replaceDynamicTagContent(clause.title, value);
                                    updateClauseBySequence(clause.sequence, { ...clause, title: newTitle });
                                }

                                // Check if this clause comes before redraftingClause
                                const redraftingClauseIndex = clauses.findIndex((element) => element.id === redraftingClause?.id);
                                const redraftingClauseIsBefore = redraftingClauseIndex > -1 && redraftingClauseIndex > index;

                                return (
                                    <Draggable key={clause.id} draggableId={String(clause.id)} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="clause-wrapper-container">
                                                <ReactSVG className="add-icon" src={AddIcon} onMouseDown={() => addNewClauseAtSequence(clause.sequence - 1)}/>  
                                                <div className={`clause-wrapper ${redraftingClause ? "redrafting" : ""} ${redraftingClause?.id === clause.id ? "redraftingThisClause" : ""}`}>
                                                    <div className="actions-wrapper">
                                                        <ReactSVG className="delete-icon" onClick={() => deleteClauseById(clause.id)} src={DeleteIcon} />
                                                        <ReactSVG className="drag-icon" {...provided.dragHandleProps} src={DragIcon} />
                                                    </div>
                                                    <div className="clause-with-title">
                                                        <TextEdit
                                                            type="textarea"
                                                            className="clause-title"
                                                            value={clause.title.replace(/<\/?[^>]+(>|$)/g, "")}
                                                            setValue={onTitleChange}
                                                            placeholder={t('drafting.optionalClauseTitle')}
                                                        />
                                                        <Clause
                                                            key={clause.id}
                                                            isDrafting={isDrafting}
                                                            instructionsRequired={instructionsRequired && !redraftingClauseIsBefore}
                                                            clause={clause}
                                                            updateClauseBySequence={updateClauseBySequence}
                                                            deleteClauseById={deleteClauseById}
                                                        />
                                                    </div>
                                                </div>
                                                <ReactSVG className="add-icon" src={AddIcon} onMouseDown={() => addNewClauseAtSequence(clause.sequence)}/>
                                            </div>
                                        )}
                                    </Draggable>
                                )
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
    </div>
    )
}

export default Clauses;